/**
 * SiteAddressBadge / SiteAddressNote — "this visit is somewhere else" markers.
 *
 * When the office hand-changes a job's site address (see
 * `job-address.service.ts`), the job's address no longer matches the customer's
 * record. That difference has to be VISIBLE — otherwise a tech who knows the
 * customer drives to the address they remember, and the office can't tell an
 * intentional one-off from a data error.
 *
 * Two surfaces:
 *   - `SiteAddressBadge` — a compact chip for dense lists (scheduler queue,
 *     jobs list, calendar cards).
 *   - `SiteAddressNote` — a small callout with the reason and the address it
 *     replaced, for detail panels and the tech job card.
 *
 * Both render nothing when the job has no override, so callers can drop them in
 * unconditionally.
 */

import { MapPinned } from 'lucide-react';
import type { Job } from '@/types/schema';
import { hasSiteAddressOverride, originalSiteAddress } from '@/services/job-address.service';

type JobLike = Pick<Job, 'addressOverride'>;

/**
 * Which palette to speak. This app has three surface families and they do NOT
 * share one, so picking the wrong tone silently produces unreadable text.
 *
 *  - `auto` — office chrome built on theme tokens. Plain Tailwind + `dark:`.
 *  - `tech` — the tech shell. Written light (`text-gray-*`, `text-amber-700`)
 *    and flipped wholesale by the `.tech-dark` remaps in index.css, so these
 *    classes are correct in BOTH tech themes. Never use `dark:` here.
 *  - `force-dark` — the always-dark brand-gradient modals (`from-brand-primary`
 *    / `.force-dark`). The light-mode shim in index.css rewrites bare
 *    `text-slate-300` and `text-amber-300` to DARK colours for light
 *    surfaces, and its force-dark carve-out only covers `text-white/*` — so
 *    this tone must use `text-white/*` plus arbitrary hex for the accent, or
 *    it goes dark-on-dark the moment the office switches to light mode.
 */
type Tone = 'auto' | 'tech' | 'force-dark';

interface BadgeProps {
  job: JobLike | null | undefined;
  /** 'xs' for dense scheduler cards, 'sm' for list rows and panels. */
  size?: 'xs' | 'sm';
  tone?: Tone;
  className?: string;
}

/**
 * Amber chip reading "One-off site". Amber (not red) on purpose — a changed
 * address is a deliberate office decision, not an error state.
 */
export function SiteAddressBadge({ job, size = 'sm', tone = 'auto', className = '' }: BadgeProps) {
  if (!hasSiteAddressOverride(job)) return null;

  const reason = job?.addressOverride?.reason?.trim();
  const original = originalSiteAddress(job);
  const title = [
    'Site address changed for this job only — the customer record is unchanged.',
    reason ? `Reason: ${reason}` : '',
    original ? `Usual address: ${original}` : '',
  ]
    .filter(Boolean)
    .join('\n');

  const dims =
    size === 'xs'
      ? 'text-[9px] px-1.5 py-0.5 gap-0.5'
      : 'text-[10px] px-2 py-0.5 gap-1';
  const icon = size === 'xs' ? 'w-2.5 h-2.5' : 'w-3 h-3';
  const text =
    tone === 'tech' ? 'text-amber-700'
    // Arbitrary hex, not `text-amber-300` — the light-mode shim rewrites that
    // class to a dark amber and the force-dark carve-out doesn't cover it.
    : tone === 'force-dark' ? 'text-[#fcd34d]'
    : 'text-amber-600 dark:text-amber-300';

  return (
    <span
      title={title}
      className={`inline-flex items-center font-bold uppercase tracking-wide rounded-full whitespace-nowrap bg-amber-500/15 border border-amber-500/30 ${text} ${dims} ${className}`}
    >
      <MapPinned className={icon} />
      One-off site
    </span>
  );
}

interface NoteProps {
  job: (JobLike & Partial<Pick<Job, 'address' | 'loc'>>) | null | undefined;
  tone?: Tone;
  /** Lead with the address itself — used on the tech card, where the callout
   *  is the tech's confirmation of where they're actually going. */
  showAddress?: boolean;
  className?: string;
}

/**
 * Fuller callout for detail surfaces: says plainly that this is a one-visit
 * address, why, and what the customer's usual address is — so nobody has to
 * open the customer record to work out which one is right.
 */
export function SiteAddressNote({
  job,
  tone = 'auto',
  showAddress = false,
  className = '',
}: NoteProps) {
  if (!hasSiteAddressOverride(job)) return null;

  const reason = job?.addressOverride?.reason?.trim();
  const original = originalSiteAddress(job);
  const setByName = job?.addressOverride?.setByName?.trim();
  const current = (job?.address || job?.loc || '').trim();

  // force-dark deliberately uses `text-white/*` (the only classes index.css
  // keeps white on brand-gradient surfaces in light mode) and an arbitrary hex
  // for the accent. `text-slate-*` / `text-amber-300` would be rewritten dark.
  const heading =
    tone === 'tech' ? 'text-amber-700'
    : tone === 'force-dark' ? 'text-[#fcd34d]'
    : 'text-amber-700 dark:text-amber-300';
  const body =
    tone === 'tech' ? 'text-gray-700'
    : tone === 'force-dark' ? 'text-white/70'
    : 'text-theme-secondary';
  const meta =
    tone === 'tech' ? 'text-gray-500'
    : tone === 'force-dark' ? 'text-white/50'
    : 'text-theme-tertiary';
  const strong =
    tone === 'tech' ? 'text-gray-900'
    : tone === 'force-dark' ? 'text-white'
    : 'text-theme-primary';

  return (
    <div
      className={`rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 ${className}`}
    >
      <div className="flex items-start gap-2">
        <MapPinned className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${heading}`} />
        <div className="min-w-0 flex-1">
          <p className={`text-[11px] font-bold uppercase tracking-wide ${heading}`}>
            One-off site address
          </p>
          {showAddress && current && (
            <p className={`mt-0.5 text-sm font-semibold leading-snug break-words ${strong}`}>
              {current}
            </p>
          )}
          {reason && (
            <p className={`mt-0.5 text-xs leading-snug break-words ${body}`}>{reason}</p>
          )}
          {original && (
            <p className={`mt-0.5 text-[11px] leading-snug break-words ${meta}`}>
              Usual address: {original}
            </p>
          )}
          {setByName && (
            <p className={`mt-0.5 text-[10px] ${meta}`}>Changed by {setByName}</p>
          )}
        </div>
      </div>
    </div>
  );
}
